import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, ReplaySubject } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { ServiceId } from '@motes-sdk/models/service-id';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

export interface CookieInfoParams {
    show: boolean,
    section?: CookieSection
}

export enum CookieSection {
    TERMS = 'TERMS',
    PRIVACY = 'PRIVACY',
    COOKIES = 'COOKIES',
    LICENSE = 'LICENSE',
    CONTACT = 'CONTACT',
    NONE = 'NONE'
}

@Injectable({ providedIn: 'root' })
export class MotesCookieService {
    private showCookieInfoSubject: BehaviorSubject<CookieInfoParams> = new BehaviorSubject({ show: false });
    private visitorKey: string = '';
    private initSubject = new ReplaySubject<boolean>(1);

    constructor(private route: ActivatedRoute, private router: Router) { }

    public showCookieInfo(bool: boolean = true, section: CookieSection = CookieSection.TERMS): void {
        this.showCookieInfoSubject.next({
            show: bool,
            section: section
        });
    }

    public watchCookieInfoStatus(): BehaviorSubject<CookieInfoParams> {
        return this.showCookieInfoSubject;
    }

    /**
     * Generate visitor key if it does not already exist
     */
    public initVisitorKey(cookiePrefix: ServiceId) : string {
        // Get existing visitorKey cookie
        this.visitorKey = this.getCookie('visitorKey');
        // Do nothing more if visitorKey is already generated (by this app or some other app)
        if(this.visitorKey) {
            this.initSubject.next(true);
            return this.visitorKey;
        }
        // Get visitor key from url and use that in case we don't have one already
        // This is cool if user has never visited the webpage before and comes directly from the app
        const urlVisitorKey = this.getQueryParam('visitorKey');

        // Generate visitor key with prefix of cookie origin
        this.visitorKey = urlVisitorKey ? urlVisitorKey : `${cookiePrefix}_${uuid()}`;
        this.setCookie('visitorKey', this.visitorKey);
        this.initSubject.next(true);
        return this.visitorKey;
    }

    public awaitInit() : Promise<boolean> {
        return firstValueFrom(this.initSubject.asObservable());
    }

    /**
     * Get the VisitorKey to track users between domains (webflow/home/app)
     * and different user sessions.
     * Not to be confused with other local storage keys that are not available across domains:
     * - visitKey: generated on each refresh of app / home (generated on first stat event)
     * - deviceKey: uniquely identifies each device across sessions
     */
    public getVisitorKey() {
        if(!this.visitorKey) {
            console.warn("Visitor key not initialized - please run initVisitorKey()");
            // Make sure that we always have a visitor key
            // await this.awaitInit();
            return "visitor_key_not_initialized";
        }
        // Slice and uri encode to make it a bit more safe to save
        return encodeURI(this.visitorKey.slice(0, 100)) || "";
    }

    private setCookie(name: string, value: string, days: number = 365 * 20) {
        if(typeof window === 'undefined' || !document) return;
        // Calculate cookie expire date
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        let cookie = `${name}=${value}; expires=${date.toUTCString()};`;

        // Detect if we are on localhost to set local cookie
        const onLocalhost = window.location?.href?.startsWith("http://localhost");
        // Add storyhunt domain as root path (share cookie between domains)
        if(!onLocalhost) {
            cookie += ` domain=${this.getTopDomain()}; path=/`;
        }
        // Set cookie
        document.cookie = cookie;
    }

    private getTopDomain() : string {
        if(typeof window === 'undefined') return "";

        var hostname = window.location.hostname;
        var parts = hostname.split('.');
        var numParts = parts.length;

        // Handle common cases like example.co.uk and example.com
        if (numParts > 2) {
            // Return the last two parts
            return parts[numParts - 2] + '.' + parts[numParts - 1];
        } else {
            // Return the hostname as is (for cases like localhost)
            return hostname;
        }
    }

    private getCookie(name: string): string | undefined {
        const cookies = document?.cookie || "";
        return cookies.split("; ").find(row => row.startsWith(`${name}=`))?.split("=")[1];
    }

    private getQueryParam(paramName: string): string {
        if(typeof window === 'undefined' || !document) return "";
        return this.route.snapshot.queryParamMap.get(paramName) || new URLSearchParams(window?.location?.search).get(paramName);
    }
}
